import { InAppState } from "../base/InAppState";
import template from "./ChatTemplate.html";

import { _lcs } from "../../i18n";

/**
 * ChatState — the "Chat" tab: the guest's conversation with their host, and
 * with the house's AI concierge where the house has it on.
 *
 * One thread. When the concierge answers it, the header says so and offers
 * "Direkt an Gastgeber schreiben" at all times; when the hosts answer, a house
 * with a concierge offers "Concierge fragen" instead. The concierge's answers
 * are marked as AI, may carry links into the app (an event, a restaurant) and,
 * when the hosts are needed, a "weitergeben?" offer under the answer.
 *
 * While the screen is open it polls for the host's replies. Re-rendering on
 * every tick would fight the guest for the scroll position, so it only repaints
 * when something actually arrived (see core/Chat.refreshThread) and only pins
 * to the bottom when the guest was already there.
 */
export class ChatState extends InAppState
{
    static ROUTE = "/chat";
    static ID = "chat";

    /**
     * How often an open thread checks for a reply.
     *
     * Far faster than the shell's background dot poll: this runs only while the
     * guest is looking at the conversation, which is exactly when a reply
     * arriving eight seconds late would be noticed. Websockets would be better
     * and are a larger change than the chat itself — the API is plain REST
     * everywhere else (see the CLAUDE.md architecture notes).
     */
    static POLL_MS = 8000;

    /**
     * Offered under an empty thread — the questions a house actually gets. The
     * German wording IS the dictionary key: a class constant is evaluated
     * before I18n exists, so the template translates on the way out. A house
     * with a concierge can set its own (they are then shown as written).
     */
    static STARTERS = [
        "Wann gibt es Frühstück?",
        "Können wir später einchecken?",
        "Habt ihr einen Tipp für heute Abend?"
    ];

    getActiveTab() { return "chat"; }

    /**
     * A thread the host can answer needs an address that has been proven to
     * work — see App.hasVerifiedAccount. Without one the guest is sent to
     * VerifyRequiredState, which explains what is missing — unless the house
     * lets guests without an account ask its concierge.
     */
    canEnter()
    {
        return super.canEnter() && this.app.chat.canOpen();
    }

    getRedirectUrl()
    {
        // Not signed in at all is still the login's job; the gate is for a
        // guest who IS in the app but has no confirmed account.
        return this.app.user === null ? super.getRedirectUrl() : "/verify-account?from=chat";
    }

    async enter()
    {
        this.chat = this.app.chat;
        this.sending = false;
        this.offerDismissed = null;

        // Bound once to the shell's content slot, which survives every render
        // of the thread inside it.
        this._bind();

        this._render();

        await this.chat.load( true );
        if ( !this.isActive() )
        {
            return;
        }

        this._render();
        this._scrollToEnd();

        if ( this.chat.isAvailable() )
        {
            // Opening the thread is what counts as reading it.
            await this.chat.markRead();

            // Tracked — the framework clears it when the screen exits.
            this.setInterval( () => this._poll(), ChatState.POLL_MS );
        }
    }

    async exit()
    {
        // The tab bar keeps showing the dot from wherever the guest goes next,
        // so the background poll takes over again.
        this.app.chat.startPolling();
        await super.exit();
    }

    /* -------------------------------- Rendering ------------------------------ */

    _render()
    {
        const lines = this.chat.getLines();
        const info = this.chat.conciergeInfo();
        const mode = this.chat.effectiveMode();
        const concierge = info.enabled === true || this.chat.isAnonymousConcierge();
        const houseName = this.chat.hostName || this.app.accommodation?.name || _lcs( "Deine Gastgeber" );

        lines.forEach( ( line ) =>
        {
            if ( line.showOffer && this.offerDismissed === line.id )
            {
                line.showOffer = false;
            }
        } );

        const starters = mode === "concierge" && Array.isArray( info.starters ) && info.starters.length
            ? info.starters.map( ( text ) => ( { text, translate : false } ) )
            : ChatState.STARTERS.map( ( text ) => ( { text, translate : true } ) );

        // A guest may start typing the next question while the concierge is
        // still answering the last one: the re-render must not eat it.
        const typing = this.app.view.pageContent.querySelector( '[data-id="input"]' );
        const draft = typing ? typing.value : "";
        const hadFocus = typing && document.activeElement === typing;

        this.renderScreen( template, {
            "mode" : mode,
            "hasConcierge" : concierge,
            "anonymous" : this.chat.isAnonymousConcierge(),
            "host" : {
                "name" : mode === "concierge" ? this._conciergeName( info, houseName ) : houseName,
                "role" : mode === "concierge" ? _lcs( "KI-Concierge · {house}", { house : houseName } ) : _lcs( "Gastgeber" ),
                "houseName" : houseName
            },
            "greeting" : mode === "concierge" ? this._conciergeGreeting( info, houseName ) : "",
            "lines" : lines,
            "failed" : this.chat.failed,
            "sending" : this.sending && mode === "concierge",
            // Only under an empty thread: once a conversation exists, three
            // canned openers are noise between the guest and what was said.
            "replies" : lines.length === 0 && !this.chat.failed ? starters : []
        } );

        if ( draft )
        {
            const input = this.app.view.pageContent.querySelector( '[data-id="input"]' );
            if ( input )
            {
                input.value = draft;
                if ( hadFocus ) input.focus();
            }
        }
    }

    /**
     * The concierge's name and first message. When the host wrote their own,
     * as written; the defaults the API sends are German, so the app words them
     * itself in the guest's language (Funktionstest F18).
     */
    _conciergeName( info, houseName )
    {
        if ( info.nameIsDefault === true || !info.name )
        {
            return _lcs( "Concierge von {house}", { house : houseName } );
        }
        return info.name;
    }

    _conciergeGreeting( info, houseName )
    {
        if ( info.greetingIsDefault === true )
        {
            return _lcs( "Willkommen im {house}! Ich bin der digitale Concierge und beantworte deine Fragen rund um den Aufenthalt — jederzeit. Für alles andere leite ich dich gern an deine Gastgeber weiter.",
                { house : houseName } );
        }
        return info.greeting || "";
    }

    _bind()
    {
        const root = this.app.view.pageContent;

        this.addEventListener( root, "submit", ( event ) =>
        {
            const form = event.target.closest( '[data-id="composer"]' );
            if ( !form ) return;
            event.preventDefault();

            const input = root.querySelector( '[data-id="input"]' );
            this._send( input ? input.value : "" );
        } );

        this.addEventListener( root, "click", ( event ) =>
        {
            const reply = event.target.closest( "[data-reply]" );
            if ( reply )
            {
                this._send( reply.getAttribute( "data-reply" ) );
                return;
            }

            const action = event.target.closest( "[data-chat-action]" );
            if ( action )
            {
                this._action( action.getAttribute( "data-chat-action" ), action );
                return;
            }

            const link = event.target.closest( "[data-route]" );
            if ( link )
            {
                event.preventDefault();
                this.app.router.redirect( link.getAttribute( "data-route" ) );
            }
        } );
    }

    /* --------------------------------- Actions -------------------------------- */

    async _action( name, el )
    {
        // Without an account the hosts cannot answer: VerifyRequiredState
        // explains why and how to get one.
        if ( ( name === "handover" || name === "offer-yes" ) && this.chat.isAnonymousConcierge() )
        {
            this.app.router.redirect( "/verify-account?from=chat" );
            return;
        }

        if ( name === "offer-no" )
        {
            this.offerDismissed = el.getAttribute( "data-line" );
            this._render();
            return;
        }

        el.disabled = true;
        try
        {
            if ( name === "handover" || name === "offer-yes" )
            {
                await this.chat.handover();
            }
            else if ( name === "concierge" )
            {
                await this.chat.askConcierge();
            }
        }
        catch ( e )
        {
            console.error( e );
            this.app.view.toast( _lcs( "Das hat nicht geklappt. Bitte versuch es noch einmal." ), "error" );
        }
        if ( this.isActive() )
        {
            this._render();
            this._scrollToEnd();
        }
    }

    /* --------------------------------- Sending ------------------------------- */

    async _send( body )
    {
        const text = String( body || "" ).trim();
        if ( !text || this.sending )
        {
            return;
        }

        const root = this.app.view.pageContent;
        const input = root.querySelector( '[data-id="input"]' );
        const send = root.querySelector( '[data-id="send"]' );

        this.sending = true;
        if ( send ) send.disabled = true;
        // Cleared before the request, not after: the guest has "sent" it, and
        // a box that keeps the text until the network answers reads as failure.
        if ( input ) input.value = "";

        // The concierge takes a second or two: show the question and a typing
        // indicator right away.
        if ( this.chat.effectiveMode() === "concierge" )
        {
            this.chat.messages.push( { "id" : "pending", "sender" : "guest", "body" : text, "createdAt" : new Date().toISOString() } );
            this._render();
            this._scrollToEnd();
            this.chat.messages = this.chat.messages.filter( ( m ) => m.id !== "pending" );
        }

        try
        {
            await this.chat.send( text );
        }
        catch ( e )
        {
            this.app.view.toast( e.message, "error" );
            // Hand the text back so it is not lost with the error.
            const box = this.app.view.pageContent.querySelector( '[data-id="input"]' );
            if ( box )
            {
                box.value = text;
                box.focus();
            }
        }
        finally
        {
            this.sending = false;
            if ( this.isActive() )
            {
                this._render();
                this._scrollToEnd();
            }
        }
    }

    /* --------------------------------- Polling ------------------------------- */

    async _poll()
    {
        // Repainting mid-send would drop the half-typed reply out of the box.
        if ( this.sending )
        {
            return;
        }

        const wasAtEnd = this._isAtEnd();
        const changed = await this.chat.refreshThread();

        if ( !changed || !this.isActive() )
        {
            return;
        }

        this._render();

        // Only follow the conversation down if the guest was already at the
        // bottom; yanking someone out of a message they scrolled back to read
        // is worse than making them scroll again.
        if ( wasAtEnd )
        {
            this._scrollToEnd();
        }

        await this.chat.markRead();
    }

    _scrollEl()
    {
        return this.app.view.pageContent.querySelector( '[data-id="scroll"]' );
    }

    _isAtEnd()
    {
        const scroll = this._scrollEl();
        if ( !scroll )
        {
            return true;
        }
        // A little slack: "at the bottom" should survive a few pixels of
        // momentum or a rounding difference between browsers.
        return scroll.scrollHeight - scroll.scrollTop - scroll.clientHeight < 48;
    }

    _scrollToEnd()
    {
        const scroll = this._scrollEl();
        if ( scroll ) scroll.scrollTop = scroll.scrollHeight;
    }
}
